import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import { Seo } from "@/components/site/Seo";
import { FinalCTA } from "@/components/site/CTAs";

const RCard = ({ cat, title, body, ask }) => (
  <div className="rcard">
    <div className="cat">{cat}</div>
    <h3>{title}</h3>
    <p>{body}</p>
    <div className="ask">{ask}</div>
  </div>
);

const Reports = () => (
  <>
    <Seo
      title="Clearview — reports"
      description="Ask in plain English. Get the document back. The reports a CFO charges thousands for — written from your live numbers, in minutes."
      path="/reports"
    />
    <Header />

    <section className="ph">
      <div className="container">
        <Reveal>
          <div className="eyebrow eyebrow-light">Reports</div>
          <h1 style={{ marginTop: 14 }}>
            Ask in plain English.<br /><span className="gtext">Get the document back.</span>
          </h1>
          <p className="lead">
            The reports a CFO charges thousands for — written from your live numbers, in minutes. You don't fill in a template. You just say what you need.
          </p>
        </Reveal>
      </div>
    </section>

    <section className="pad">
      <div className="container">
        <Reveal className="flow">
          <div className="prompt">"I want to sell the business — is it looking good to a buyer?"</div>
          <div className="arrow">→</div>
          <div className="docmini">
            <div className="bar" />
            <div className="dm">
              <div className="dt">Sale Readiness Report</div>
              <div className="ln" />
              <div className="ln" />
              <div className="ln s" />
            </div>
          </div>
        </Reveal>
        <Reveal className="center sub-h" style={{ marginTop: 34 }}>
          That's the whole interaction. No forms, no chart-building, no waiting for month-end. A real document, built from your actual data.
        </Reveal>
      </div>
    </section>

    <section className="band-grey pad">
      <div className="container">
        <Reveal className="center">
          <div className="eyebrow">The library</div>
          <h2 style={{ fontSize: "clamp(28px,4.4vw,42px)", color: "var(--navy)", marginTop: 14 }}>
            Whatever the moment needs.
          </h2>
        </Reveal>
        <Reveal className="rgrid" style={{ marginTop: 44 }}>
          <RCard cat="Borrowing" title="Bank Loan Pack"
            body="Lender-ready financials and the narrative to go with them, straight from your numbers."
            ask={'"Put together what the bank will want."'} />
          <RCard cat="Exit" title="Sale Readiness"
            body="An honest read on whether the business is buyer-ready — and what to fix first."
            ask={'"Is it looking good to a buyer?"'} />
          <RCard cat="Board" title="Board Report"
            body="The month in one clean page — the numbers that matter and what they mean."
            ask={'"Write up this month for the board."'} />
          <RCard cat="Cash" title="Cash Crisis Report"
            body="What to do tonight if it's tight: what's due, what to chase, what to hold."
            ask={'"Cash is tight — what do I do this week?"'} />
          <RCard cat="Debtors" title="Debtor Collection"
            body="Who to chase, in what order, with the email scripts to send them."
            ask={'"Who owes me and how do I get it in?"'} />
          <RCard cat="Margin" title="Profit Leak Report"
            body="Where the money's quietly slipping — jobs, customers, costs — ranked by size."
            ask={'"Where am I losing margin?"'} />
        </Reveal>
        <p className="center sub-h" style={{ marginTop: 30 }}>
          And if you ask for something that isn't a named report, it writes that too — the prompt is the product.
        </p>
      </div>
    </section>

    <section className="pad">
      <div className="container">
        <Reveal className="center">
          <div className="eyebrow">A real one</div>
          <h2 style={{ fontSize: "clamp(26px,4vw,38px)", color: "var(--navy)", marginTop: 14 }}>
            What comes back.
          </h2>
        </Reveal>
        <Reveal className="sample">
          <div className="bar" />
          <div className="sp">
            <div className="meta">
              <span>Sale Readiness Report · Travel Trucks Pty Ltd</span>
              <span>Generated from Xero · 19 Jun 2026</span>
            </div>
            <h2 className="title">Sale Readiness</h2>
            <div className="verdict">
              Verdict: <span className="g">Nearly ready — fix two things first.</span> On today's numbers a buyer would pay, but two items will cost you on price if you don't clean them up before going to market.
            </div>
            <div className="sec">
              <div className="h">The headline numbers</div>
              <table>
                <tbody>
                  <tr><td>Revenue (trailing 12mo)</td><td>$6.12M</td></tr>
                  <tr><td>Normalised EBITDA</td><td>$842k</td></tr>
                  <tr><td>Owner add-backs identified</td><td>$118k</td></tr>
                  <tr><td>Indicative range (3–4× EBITDA)</td><td>$2.5–3.4M</td></tr>
                </tbody>
              </table>
            </div>
            <div className="sec">
              <div className="h">What a buyer will mark you down for</div>
              <p>Revenue concentration: your top customer is 31% of sales. Buyers discount for this. And $61k of the add-backs aren't documented — defensible, but you'll need the paper trail before due diligence.</p>
            </div>
            <div className="flag">
              <b>⚡ Do first:</b> document the $61k of add-backs and start a second anchor customer. Both lift the price you'll be offered more than any cost-cutting would.
            </div>
          </div>
        </Reveal>
        <p className="center" style={{ marginTop: 24, color: "var(--muted-2)", fontSize: 13 }}>
          Illustrative sample on demo data. Your report is written from your own live numbers.
        </p>
      </div>
    </section>

    <FinalCTA
      title="See it write yours."
      highlight="In minutes."
      body="Connect read-only and we'll show you the document you actually need — on your real numbers."
      testIdPrefix="reports-final"
    />

    <Footer />
  </>
);

export default Reports;
